/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, March 18th 2022, 7:38:18 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { EmailTagsFormInput } from "ui-widgets/forms";
import { Grid, Typography } from "@mui/material";
import { COLORSCHEME } from "theme/styles";
import { OSAPIForm, EmphasisText } from "ui-widgets";
import { UserInvitation } from "lib/schemas";
import { SingleDepartmentSelectInput } from "organizations/components/DeparmentSelectorInputs";
import { PortalsCheckboxesInput } from "portals/components/PortalSelectorInputs";
import { WorkerTypeDropDownInput } from "workers";
import { useInvitationAPI } from "onboarding/services/InvitationAPI";
import { Container } from "@mui/material";
import { StepContinueButton } from "./StepContinueButton";
import { SkipStepButton } from "./SkipStepButton";
import { useOnboardingContext } from "onboarding/providers/OnboardingProvider";
import { useGlobalScope } from "core/providers";

export const SetupInviteWorkersStep = ({ label }) => {
  const { handleNextStep } = useOnboardingContext();

  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const onSuccessfulPost = (invitation: UserInvitation) => handleNextStep();

  const { useInviteWorkersAPIForm } = useInvitationAPI();

  const { useAPIFormReturn, workerInvitation } = useInviteWorkersAPIForm();

  React.useEffect(() => {
    if (useAPIFormReturn.isSuccessfulAPIDataSend) onSuccessfulPost(workerInvitation);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAPIFormReturn.isSuccessfulAPIDataSend, JSON.stringify(workerInvitation)]);

  return selectedOrganization?.id ? (
    <div className="banner-content-style-A">
      <Container maxWidth="sm">
        <h1 style={{ color: COLORSCHEME.primary }}>{label}</h1>

        <p className="lead">
          You are almost done. Invite your co-workers at <EmphasisText>{selectedOrganization.name}</EmphasisText> to
          collaborate on Onestop. You can also do this later.
        </p>

        <OSAPIForm useAPIFormReturn={useAPIFormReturn} showSubmitButton={false}>
          <p>
            <Typography variant="subtitle2">Enter Email Addresses</Typography>
          </p>
          <EmailTagsFormInput
            schema_field_key="invitee_emails"
            fullWidth
            placeholder="Enter the email address of the worker to invite"
          />

          <p>
            <Typography variant="subtitle2">Select department</Typography>
          </p>
          <SingleDepartmentSelectInput<UserInvitation> schema_field_key="department" label="" />

          <p>
            <Typography variant="subtitle2">Invite as:</Typography>
          </p>
          <WorkerTypeDropDownInput<UserInvitation> schema_field_key="invited_as" />

          <p>
            <Typography variant="subtitle2">Select portal(s)</Typography>
          </p>
          <PortalsCheckboxesInput schema_field_key="portals" />

          <div className="banner-cta-style-A text-center">
            <p style={{ color: COLORSCHEME.primary }} className="progress-text">
              4/4 Next: Billing Details
            </p>
            <Grid container spacing={3} columns={12}>
              <Grid item xs={12}>
                <StepContinueButton isLoading={useAPIFormReturn.isSendingDataToAPI} />
              </Grid>
              <Grid item xs={12}>
                <SkipStepButton handleNextStep={handleNextStep} />
              </Grid>
            </Grid>
          </div>
        </OSAPIForm>
      </Container>

      <div className="banner-content-style-A-footer">
        <div className="footer-logo-style-A">
          <img className="image-style-B footer-A-logo" src="/img/onestop-symbol-in-circle.svg" alt="OneStop" />
        </div>
      </div>
    </div>
  ) : null;
};
